<template>
  <div>
    <!-- Header -->
    <div class="flex items-start justify-between mb-4">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span v-if="!isNew" class="h-2 w-2 rounded-full" :class="statusDotClass(routine.last_run_status)"></span>
          <h3 class="text-sm font-semibold text-gray-900 truncate">
            {{ isNew ? 'New parse routine' : routine.name }}
          </h3>
        </div>
        <p v-if="!isNew" class="text-xs text-gray-400 mt-0.5">
          Last run {{ formatDateTime(routine.last_run_at) }}
          <template v-if="routine.last_run_status"> · {{ statusKey(routine.last_run_status) }}</template>
        </p>
      </div>
      <div v-if="canManage" class="flex items-center gap-2 shrink-0 ml-4">
        <template v-if="!editing">
          <button
            class="text-xs px-2.5 py-1 rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
            :disabled="running"
            @click="runNow"
          >
            {{ running ? 'Running…' : 'Run now' }}
          </button>
          <button class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400" @click="beginEdit">
            Edit
          </button>
          <button class="text-xs px-2.5 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50" @click="deleteModalOpen = true">
            Delete
          </button>
        </template>
        <template v-else>
          <button class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400" @click="cancelEdit">
            Cancel
          </button>
          <button
            class="text-xs px-2.5 py-1 rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
            :disabled="saving || !draft.name || !draft.s3_source_prefix"
            @click="save"
          >
            {{ saving ? 'Saving…' : (isNew ? 'Create' : 'Save') }}
          </button>
        </template>
      </div>
    </div>

    <!-- Config -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Name</label>
        <input
          v-model.trim="draft.name"
          :disabled="!editing"
          type="text"
          class="w-full border border-gray-200 rounded px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:border-gray-400"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">S3 source prefix</label>
        <input
          v-model.trim="draft.s3_source_prefix"
          :disabled="!editing"
          type="text"
          placeholder="e.g. quality_gaps/aetna/"
          class="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:border-gray-400"
        />
      </div>
    </div>
    <label class="inline-flex items-center gap-2 text-sm text-gray-700 mb-4">
      <input v-model="draft.recursive" type="checkbox" :disabled="!editing" />
      Recursive — scan nested folders under the prefix
    </label>

    <!-- Schemas on this routine -->
    <div class="mb-6">
      <p class="text-xs font-medium text-gray-500 mb-1.5">
        Schemas
        <span class="text-gray-400 font-normal">— files under the prefix are matched against these</span>
      </p>
      <div v-if="editing" class="border border-gray-100 rounded max-h-56 overflow-y-auto p-2 space-y-1">
        <label
          v-for="schema in allSchemas"
          :key="schema.id"
          class="flex items-center gap-2 text-xs text-gray-700 hover:bg-gray-50 rounded px-1.5 py-1 cursor-pointer"
        >
          <input
            type="checkbox"
            :checked="draft.schemas.includes(schema.id)"
            @change="toggleSchema(schema.id, $event.target.checked)"
          />
          <span class="font-mono">{{ schema.table_name }}</span>
          <span class="text-gray-400">{{ sheetTypeLabel(schema.sheet_type) }}</span>
        </label>
        <p v-if="!allSchemas.length" class="text-xs text-gray-400 px-1.5 py-1">
          No schemas yet — use Discover to create one from a file.
        </p>
      </div>
      <div v-else class="flex flex-wrap gap-1.5">
        <button
          v-for="schema in attachedSchemas"
          :key="schema.id"
          class="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded px-2 py-0.5 font-mono"
          @click="$emit('select-schema', schema.id)"
        >
          {{ schema.table_name }}
        </button>
        <span v-if="!attachedSchemas.length" class="text-xs text-amber-600">
          none — this routine scans its prefix but can't parse anything
        </span>
      </div>
    </div>

    <!-- Scoped history -->
    <div v-if="!isNew">
      <p class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Parse history</p>
      <ParseHistoryTable ref="history" :key="'routine-history-' + routine.id" :routine-id="routine.id" :can-manage="canManage" />
    </div>

    <!-- Delete confirmation -->
    <div
      v-if="deleteModalOpen"
      class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center"
      @click.self="deleteModalOpen = false"
    >
      <div class="bg-white rounded-lg border border-gray-200 w-96 p-4">
        <h4 class="text-sm font-semibold text-gray-900 mb-2">Delete routine</h4>
        <p class="text-xs text-gray-600 mb-3">
          "{{ routine.name }}" will stop scanning {{ routine.s3_source_prefix }}. Its schemas and
          parse history are kept.
        </p>
        <div class="flex items-center justify-end gap-2">
          <button class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600" @click="deleteModalOpen = false">
            Cancel
          </button>
          <button
            class="text-xs px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            :disabled="deleting"
            @click="doDelete"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  formatDateTime, statusDotClass, statusKey, sheetTypeLabel,
} from '@/utils/etlFormat';
import ParseHistoryTable from './ParseHistoryTable.vue';

export default {
  name: 'RoutineDetail',
  components: { ParseHistoryTable },
  props: {
    // Pass { id: null } for create mode.
    routine: { type: Object, required: true },
    allSchemas: { type: Array, default: () => [] },
    canManage: { type: Boolean, default: false },
  },
  emits: ['changed', 'deleted', 'toast', 'select-schema'],
  data() {
    return {
      draft: this.cloneRoutine(this.routine),
      editing: !this.routine.id,
      saving: false,
      running: false,
      deleteModalOpen: false,
      deleting: false,
    };
  },
  computed: {
    isNew() {
      return !this.routine.id;
    },
    attachedSchemas() {
      return this.allSchemas.filter((s) => (this.routine.schemas || []).includes(s.id));
    },
  },
  watch: {
    routine(next) {
      this.draft = this.cloneRoutine(next);
      this.editing = !next.id;
      this.deleteModalOpen = false;
    },
  },
  methods: {
    formatDateTime,
    statusDotClass,
    statusKey,
    sheetTypeLabel,
    cloneRoutine(routine) {
      return {
        name: routine.name || '',
        s3_source_prefix: routine.s3_source_prefix || '',
        recursive: routine.recursive || false,
        schemas: [...(routine.schemas || [])],
      };
    },
    beginEdit() {
      this.draft = this.cloneRoutine(this.routine);
      this.editing = true;
    },
    cancelEdit() {
      this.draft = this.cloneRoutine(this.routine);
      this.editing = false;
      if (this.isNew) this.$emit('deleted', null); // close the create pane
    },
    toggleSchema(id, checked) {
      this.draft.schemas = checked
        ? [...this.draft.schemas, id]
        : this.draft.schemas.filter((s) => s !== id);
    },
    async save() {
      this.saving = true;
      try {
        const { data } = this.isNew
          ? await axios.post('/api/etl_module/parse-routines/', this.draft)
          : await axios.put(`/api/etl_module/parse-routines/${this.routine.id}/`, this.draft);
        this.editing = false;
        this.$emit('changed', data);
        this.$emit('toast', { message: `Routine "${data.name}" ${this.isNew ? 'created' : 'saved'}`, kind: 'success' });
      } catch (err) {
        const detail = err?.response?.data;
        const message = typeof detail === 'object' && detail !== null
          ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join('; ')
          : 'Failed to save routine';
        this.$emit('toast', { message, kind: 'error' });
      }
      this.saving = false;
    },
    async runNow() {
      this.running = true;
      try {
        const { data } = await axios.post(`/api/etl_module/parse-routines/${this.routine.id}/trigger/`);
        this.$emit('toast', {
          message: `"${this.routine.name}": ${data.files_processed || 0} files processed, `
            + `${(data.rows_loaded || 0).toLocaleString()} rows loaded, ${data.files_skipped || 0} skipped`,
          kind: 'success',
        });
        this.$emit('changed', null);
        this.$refs.history?.refresh();
      } catch (err) {
        this.$emit('toast', {
          message: err?.response?.data?.error || `Failed to run "${this.routine.name}"`,
          kind: 'error',
        });
      }
      this.running = false;
    },
    async doDelete() {
      this.deleting = true;
      try {
        await axios.delete(`/api/etl_module/parse-routines/${this.routine.id}/`);
        this.deleteModalOpen = false;
        this.$emit('deleted', this.routine.id);
        this.$emit('toast', { message: `Routine "${this.routine.name}" deleted`, kind: 'success' });
      } catch (err) {
        this.$emit('toast', {
          message: err?.response?.data?.error || 'Failed to delete routine',
          kind: 'error',
        });
      }
      this.deleting = false;
    },
  },
};
</script>
